import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSubjectLanguage } from '@features/user';

interface TutorialPromptProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
  tutorialKey?: string;
  title?: string;
  description?: string;
  customHeader?: string;
  customDescription?: string;
  hideSubtext?: boolean;
  customAcceptText?: string;
  customDeclineText?: string;
  customContent?: React.ReactNode;
}

export const TutorialPrompt: React.FC<TutorialPromptProps> = ({
  open,
  onAccept,
  onDecline,
  tutorialKey = 'default',
  title,
  description,
  customHeader,
  customDescription,
  hideSubtext = false,
  customAcceptText = 'Show Tutorial',
  customDeclineText = 'Skip',
  customContent,
}) => {
  const theme = useTheme();
  const subjectLanguage = useSubjectLanguage();

  const getPromptTitle = () => {
    if (customHeader) return customHeader;
    if (title) return title;
    if (tutorialKey === 'schedule-view') {
      return 'Welcome to your Appointment Calendar!';
    }
    return 'Looks like this is your first time here!';
  };

  const getPromptDescription = () => {
    if (customDescription) return customDescription;
    if (description) return description;
    if (tutorialKey === 'schedule-view') {
      return `Would you like a quick tour of how to schedule and manage ${subjectLanguage} appointments?`;
    }
    return `Would you like to see a quick tutorial of how to use JotPsych to record a ${subjectLanguage} encounter and generate an encounter note?`;
  };

  return (
    <Dialog
      open={open}
      fullWidth
     maxWidth={"sm"} slotProps={{ paper: {
        sx: {
          p: 2,
          border: 1,
          borderRadius: '0.75rem',
          borderColor: 'borderColors.primary',
          animation: 'float 2s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0)',
            },
            '50%': {
              transform: 'translateY(-3px)',
            },
          },
        },
      } }}>
      {customContent ? (
        customContent
      ) : (
        <>
          <DialogTitle sx={{ p: 0, mb: 2 }}>
            <Typography variant="h6">{getPromptTitle()}</Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, mb: 2 }}>
            <Typography>{getPromptDescription()}</Typography>
            {!hideSubtext && (
              <Typography sx={{ mt: 2 }}>This should take less than 1 minute!</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 0, gap: 1 }}>
            <Button onClick={onDecline} color="inherit">
              {customDeclineText}
            </Button>
            <Button onClick={onAccept} variant="contained" autoFocus>
              {customAcceptText}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
